/** SA-1060 (spec 2026-10-09 §2.4, ruling Q3): a card is "edited" exactly when
 * the publish dialog would list it. Customize: the sparse overlay has an entry
 * for the step / a seat / a field the base declares — the server's
 * `summarize_overlay` process branch, one ref per patched entry. Edit: the
 * server's `process_definition_diff` names the step, or any change touches
 * the seats, fields or lanes section. */
import type { ProcessDefinitionDiff, ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import type { WorkflowOverlayJson } from '@/types/workflowConfig'
import { FIELDS_CARD, LANES_CARD, SEATS_CARD, stepCard, type CardId } from './editorCards'

const isPatch = (v: unknown): boolean => !!v && typeof v === 'object' && !Array.isArray(v)

type Sections = { steps?: Record<string, unknown>; seats?: Record<string, unknown>; fields?: Record<string, unknown> }

export function editedFromOverlay(overlay: WorkflowOverlayJson, base: ProcessWorkflowDefinitionJson): Set<CardId> {
  const out = new Set<CardId>()
  const o = overlay as Sections
  for (const [id, patch] of Object.entries(o.steps ?? {})) {
    if (isPatch(patch) && id in (base.steps ?? {})) out.add(stepCard(id))
  }
  if (Object.entries(o.seats ?? {}).some(([id, p]) => isPatch(p) && id in (base.seats ?? {}))) out.add(SEATS_CARD)
  if (Object.entries(o.fields ?? {}).some(([id, p]) => isPatch(p) && id in (base.fields ?? {}))) out.add(FIELDS_CARD)
  return out
}

export function editedFromDiff(diff: ProcessDefinitionDiff | null): Set<CardId> {
  const out = new Set<CardId>()
  if (!diff) return out
  for (const id of [...diff.steps_added, ...diff.exits_changed, ...diff.executors_changed, ...diff.contracts_changed]) {
    out.add(stepCard(id))
  }
  if (diff.seats_added.length > 0 || diff.seats_removed.length > 0) out.add(SEATS_CARD)
  if (diff.fields_added.length > 0 || diff.fields_removed.length > 0 || (diff.fields_changed ?? []).length > 0) out.add(FIELDS_CARD)
  if (diff.lanes_added.length > 0 || diff.lanes_removed.length > 0) out.add(LANES_CARD)
  return out
}
